import { Content } from "pdfmake/interfaces";
import { sortArray } from "components/results/analysis-results/sortUtil";
import text from "components/results/analysis-results/text";
import gap from "report/common/gap";
import icons from "report/common/svg";
import getTitle from "report/common/title";
import renderLineChartSvg from "./echart-to-svg";
import getLegendLayout from "./legend-layout";
import { LineLegendRow } from "./legend-table";

type LineSeriesPoint = { value?: number; intensity?: number } | number | null;

type LineSeries = {
  name?: string;
  data?: LineSeriesPoint[];
  [key: string]: any;
};

type LineSeriesDataPoint = {
  longitude?: string | number;
  latitude?: string | number;
  lng?: string | number;
  lat?: string | number;
  value?: number;
  intensity?: number;
  [key: string]: any;
};

const CHART_WIDTH = 820;
const CHART_HEIGHT = 260;

const roundToTwoDecimals = (value: number) => Math.round(value * 100) / 100;

const clampColorValue = (value: number) =>
  Math.max(0, Math.min(255, Math.round(value)));

const toHex = (value: number) =>
  clampColorValue(value).toString(16).padStart(2, "0");

const normalizeLegendColor = (color?: string): string => {
  if (!color || typeof color !== "string") {
    return "#4B5563";
  }

  const trimColor = color.trim();

  if (/^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/.test(trimColor)) {
    return trimColor;
  }

  const rgbMatch = trimColor.match(
    /^rgba?\(\s*(\d{1,3})\s*,\s*(\d{1,3})\s*,\s*(\d{1,3})(?:\s*,\s*(\d*\.?\d+))?\s*\)$/i
  );

  if (rgbMatch) {
    const r = Number(rgbMatch[1]);
    const g = Number(rgbMatch[2]);
    const b = Number(rgbMatch[3]);
    return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
  }

  return "#4B5563";
};

const getLngLatFromPoint = (
  point?: LineSeriesDataPoint | null
): [number, number] | null => {
  const lng = Number(point?.longitude ?? point?.lng);
  const lat = Number(point?.latitude ?? point?.lat);

  if (!Number.isFinite(lng) || !Number.isFinite(lat)) {
    return null;
  }

  return [lng, lat];
};

const getDistanceBetweenPointsInMeters = (
  previous: [number, number],
  current: [number, number]
) => {
  const earthRadiusInMeters = 6378137;
  const toRadians = (value: number) => (value * Math.PI) / 180;

  const deltaLat = toRadians(current[1] - previous[1]);
  const deltaLng = toRadians(current[0] - previous[0]);
  const lat1 = toRadians(previous[1]);
  const lat2 = toRadians(current[1]);

  const a =
    Math.sin(deltaLat / 2) * Math.sin(deltaLat / 2) +
    Math.cos(lat1) *
      Math.cos(lat2) *
      Math.sin(deltaLng / 2) *
      Math.sin(deltaLng / 2);

  return 2 * earthRadiusInMeters * Math.asin(Math.sqrt(a));
};

const normalizeSeries = (series: LineSeries[]) => {
  const maxLength = series.reduce(
    (max, item) => Math.max(max, item?.data?.length || 0),
    0
  );

  const xAxisData = Array.from({ length: maxLength }, (_, i) => `${i + 1}`);

  const normalized = series.map((item) => {
    const lineData = Array.from({ length: maxLength }, (_, i) => {
      const point = item?.data?.[i];
      if (typeof point === "number") {
        return point;
      }
      if (point && typeof point === "object") {
        if (typeof point.value === "number") {
          return point.value;
        }
        if (typeof point.intensity === "number") {
          return point.intensity;
        }
      }
      return null;
    });

    return {
      name: item.name || "",
      type: "line",
      smooth: true,
      data: lineData,
      showSymbol: false,
      connectNulls: false,
      lineStyle: {
        width: 2,
        ...(item.lineStyle || {})
      },
      ...(item || {})
    };
  });

  return { xAxisData, normalizedSeries: normalized };
};

const getDistanceAxisData = (series: LineSeries[]) => {
  const firstSeriesWithPoints =
    series.find((item) => item?.data?.length) || series[0];
  const points = (firstSeriesWithPoints?.data || []) as LineSeriesDataPoint[];

  if (!points.length) {
    return { xAxisData: [], axisUnit: "m" as const };
  }

  let cumulativeDistanceInMeters = 0;
  const xAxisData: number[] = [];

  points.forEach((point, index) => {
    if (index === 0) {
      xAxisData.push(0);
      return;
    }

    const previous = getLngLatFromPoint(points[index - 1]);
    const current = getLngLatFromPoint(point);

    if (previous && current) {
      const segmentDistance = getDistanceBetweenPointsInMeters(
        previous,
        current
      );
      if (Number.isFinite(segmentDistance) && segmentDistance > 0) {
        cumulativeDistanceInMeters += segmentDistance;
      }
    }

    xAxisData.push(Math.round(cumulativeDistanceInMeters * 10000) / 10000);
  });

  const axisUnit =
    cumulativeDistanceInMeters >= 1000 ? ("km" as const) : ("m" as const);

  return {
    xAxisData:
      axisUnit === "km"
        ? xAxisData.map((value) => roundToTwoDecimals(value / 1000))
        : xAxisData.map((value) => roundToTwoDecimals(value)),
    axisUnit
  };
};

const getLayerSortIndex = (layerId?: string) => {
  if (!layerId) {
    return Number.MAX_SAFE_INTEGER;
  }
  const index = sortArray.indexOf(layerId);
  return index === -1 ? Number.MAX_SAFE_INTEGER : index;
};

const getLegendMetricSuffix = (t: any, layerId: string): string => {
  if (layerId.includes("Fathom")) {
    return t("legend.Category Score/10") || "Category Score/10";
  }

  return t("legend.Intensity Value") || "Intensity Value";
};

const getLegendRows = (t: any, series: LineSeries[]): LineLegendRow[] => {
  const sortedSeries = [...series].sort((a, b) => {
    const indexA = getLayerSortIndex(a?.name);
    const indexB = getLayerSortIndex(b?.name);
    return indexA - indexB;
  });

  const seen = new Set<string>();

  return sortedSeries.reduce((acc: LineLegendRow[], item) => {
    const layerId = item?.name || "";
    if (!layerId || seen.has(layerId)) {
      return acc;
    }

    seen.add(layerId);

    const lineColor = normalizeLegendColor(item?.lineStyle?.color);
    const layerName = t(`layerId.${layerId}`) || layerId;
    const suffix = getLegendMetricSuffix(t, layerId);

    acc.push({
      layerId,
      layerName: `${layerName}（${suffix}）`,
      lineColor,
      iconSvg: icons[layerId]
    });

    return acc;
  }, []);
};

const getLineChart = async (
  t: any,
  series: LineSeries[],
  ignoredLayerIds: string[] = []
): Promise<Content[]> => {
  if (!series || series.length === 0) {
    return [];
  }

  const { xAxisData: distanceAxisData, axisUnit } = getDistanceAxisData(series);
  const { normalizedSeries } = normalizeSeries(series);
  const legendRows = getLegendRows(t, normalizedSeries);
  const idText = text(t, ignoredLayerIds);
  const legendData = normalizedSeries.map((item) => {
    const layerId = item?.name;
    if (!layerId) {
      return "";
    }
    return t(`layerId.${layerId}`) || layerId;
  });

  const xAxisName = t("drawShape.distanceWithUnit", {
    unit: t(`drawShape.${axisUnit}`)
  });

  const option = {
    backgroundColor: "#fff",
    grid: {
      left: 24,
      right: 20,
      top: 20,
      bottom: 60
    },
    tooltip: {
      trigger: "axis"
    },
    xAxis: {
      type: "category",
      boundaryGap: false,
      data: distanceAxisData,
      name: xAxisName,
      nameLocation: "middle",
      nameGap: 35,
      nameTextStyle: {
        color: "#666",
        fontSize: 10
      },
      axisLine: {
        show: true,
        lineStyle: {
          color: "#ccd6eb"
        }
      },
      axisLabel: {
        color: "#666",
        formatter: (value: any) => {
          const numberValue = Number(value);
          if (!Number.isFinite(numberValue)) {
            return value;
          }

          return numberValue.toFixed(2);
        }
      },
      splitLine: {
        show: false
      }
    },
    yAxis: {
      type: "value",
      axisLine: {
        show: false
      },
      axisTick: {
        show: false
      }
    },
    series: normalizedSeries.map((item, index) => ({
      ...item,
      name: legendData[index]
    }))
  };

  const svg = await renderLineChartSvg(option, CHART_WIDTH, CHART_HEIGHT);

  return [
    getTitle(t("drawShape.intensityOrRiskScore"), 10),
    gap(3),
    {
      svg
    },
    gap(3),
    getLegendLayout(legendRows),
    idText
  ];
};

export default getLineChart;
